import { createListenerMiddleware } from '@reduxjs/toolkit'
import { debounce, uniqBy } from 'lodash'
import { enhancedTmsApiService } from '#api/enhancements/enhancedTmsApiService'
import type { RootState } from '#app/store'
import { toastError } from '#features/ErrorHandling/toastError'
import { toaster } from '#features/Toasts/toaster'
import { type CurrentStoredUserData, currentStoredUserDataVersion, migrateUserDataToCurrent } from '#features/UserData/CurrentStoredUserData'
import { isStoredUserData, userDataType } from '#features/UserData/StoredUserData'
import { store } from '#store/root'
import { isUserDataAction, replaceUserData } from '#store/slices/userDataSlice'

export const makeUserDataPersistence = () => {
  const userDataPersistence = createListenerMiddleware<RootState>()

  const makeUserData = (rootState: RootState): CurrentStoredUserData => {
    return {
      __version: currentStoredUserDataVersion,
      __updatedAt: Date.now(),
      __type: userDataType,
      __maicVersion: `v${VITE_APP_VERSION} (${VITE_COMMIT_SHA})`,
      ...structuredClone(rootState.userData),
    }
  }

  const debouncedSave = debounce(async () => {
    const userData = makeUserData(store.getState())
    console.log('### save user data', userData)

    try {
      const { error } = await store.dispatch(
        enhancedTmsApiService.endpoints.postUserdataUpdate.initiate({
          userData: {
            userData,
          },
        }),
      )

      if (error) {
        toaster.add({
          title: 'Failed to save user data',
          appearance: 'error',
        })
      }
    } catch (e) {
      console.error('Failed to save user data', e)
      toaster.add({
        title: 'Failed to save user data',
        appearance: 'error',
      })
    }
  }, 500)

  userDataPersistence.startListening({
    predicate: (action) => {
      return isUserDataAction(action)
    },
    effect: (_action, _api) => {
      void debouncedSave()
    },
  })

  return userDataPersistence
}

export const parseUserData = (storedData: unknown): CurrentStoredUserData | undefined => {
  if (!isStoredUserData(storedData)) {
    if (storedData) {
      console.warn('Ignoring invalid serialized user data:', storedData)
    }
    return undefined
  }

  try {
    const migratedData = migrateUserDataToCurrent(storedData)

    if (migratedData !== storedData) {
      console.log('Migrated user data from version', storedData.__version, 'to', migratedData.__version, {
        storedData,
        migratedUserData: migratedData,
      })
    }

    if (migratedData.__version !== currentStoredUserDataVersion) {
      console.warn(`Ignoring serialized user data, version mismatch: Expected '${currentStoredUserDataVersion}', received '${storedData.__version}'`)
      return undefined
    }

    return migratedData
  } catch (e) {
    console.error('Failed to parse stored user data', e)
  }

  return undefined
}

export async function loadInitialUserData() {
  const { error, data } = await store.dispatch(enhancedTmsApiService.endpoints.getUserdataLoad.initiate())

  if (error) {
    // TODO silenced as the API is not working yet with new auth
    toastError(error, { customMessage: 'Failed to load user data' })
    return
  }

  const parsed = parseUserData(data?.userData)

  if (parsed) {
    const { favorites, recentManCodes } = store.getState().userData
    const mergedManCodesArray = uniqBy([...recentManCodes.manCodes, ...parsed.recentManCodes.manCodes], 'manCode')
    store.dispatch(
      replaceUserData({
        favorites: {
          favoriteSets: parsed.favorites.favoriteSets,
          activeFavoriteSetId: favorites.activeFavoriteSetId,
        },
        recentManCodes: {
          manCodes: mergedManCodesArray,
        },
      }),
    )
  }
}
